import { useState, useEffect, useMemo } from 'react'
import DashboardLayout from '../Layout/DashboardLayout'
import axios from 'axios'
import { BASE_URL } from '../apiConfig'
import Cookies from 'js-cookie'
import { toast } from 'sonner'
import { useTranslation } from '../LanguageContext'
import { useAuth } from '../AuthContext'
import Loading from '../element/Loading'
import { PlusCircle, SquarePen, Trash2, X, Check, Shield } from 'lucide-react'

export default function RolesPage() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const token = Cookies.get('WEB_SSR')

  const [roles, setRoles] = useState([])
  const [allPermissions, setAllPermissions] = useState([])
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingRole, setEditingRole] = useState(null)
  const [saving, setSaving] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [deleting, setDeleting] = useState(false)

  const [form, setForm] = useState({ name: '' })
  const [selectedPerms, setSelectedPerms] = useState([])

  const headers = {
    Authorization: `Bearer ${token}`,
    Accept: 'application/json',
    'Content-Type': 'application/json',
  }

  const fetchData = async () => {
    setLoading(true)
    try {
      const [rolesRes, permsRes] = await Promise.all([
        axios.get(`${BASE_URL}/api/roles`, { headers }),
        axios.get(`${BASE_URL}/api/permissions`, { headers }),
      ])
      if (rolesRes.data.success) setRoles(rolesRes.data.data || [])
      if (permsRes.data.success) setAllPermissions(permsRes.data.data || [])
    } catch (error) {
      console.error(error)
      toast.error('Failed to load data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchData() }, [])

  const groupedPermissions = useMemo(() => {
    return allPermissions.reduce((acc, p) => {
      if (!acc[p.group]) acc[p.group] = []
      acc[p.group].push(p)
      return acc
    }, {})
  }, [allPermissions])

  const openAddModal = () => {
    setEditingRole(null)
    setForm({ name: '' })
    setSelectedPerms([])
    setModalOpen(true)
  }

  const openEditModal = (role) => {
    setEditingRole(role)
    setForm({ name: role.name })
    setSelectedPerms(role.permissions.map(p => p.id))
    setModalOpen(true)
  }

  const togglePermission = (permId) => {
    setSelectedPerms(prev =>
      prev.includes(permId) ? prev.filter(id => id !== permId) : [...prev, permId]
    )
  }

  const handleSave = async () => {
    if (!form.name) {
      toast.error('Role name is required')
      return
    }

    setSaving(true)
    try {
      const payload = { name: form.name, permissions: selectedPerms }

      if (editingRole) {
        const response = await axios.put(`${BASE_URL}/api/roles/${editingRole.id}`, payload, { headers })
        if (response.data.success) {
          toast.success(t('roles.update_success'))
          fetchData()
          setModalOpen(false)
        }
      } else {
        const response = await axios.post(`${BASE_URL}/api/roles`, payload, { headers })
        if (response.data.success) {
          toast.success(t('roles.add_success'))
          fetchData()
          setModalOpen(false)
        }
      }
    } catch (error) {
      console.error(error)
      toast.error(t('roles.error.saving'))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id) => {
    setDeleting(true)
    try {
      const response = await axios.delete(`${BASE_URL}/api/roles/${id}`, { headers })
      if (response.data.success) {
        toast.success(t('roles.delete_success'))
        fetchData()
        setDeleteConfirm(null)
      }
    } catch (error) {
      console.error(error)
      toast.error(t('roles.error.deleting'))
    } finally {
      setDeleting(false)
    }
  }

  // if (!can('roles.manage')) {
  //   return (
  //     <DashboardLayout>
  //       <div className="flex items-center justify-center min-h-[50vh]">
  //         <p className="text-gray-500">Unauthorized</p>
  //       </div>
  //     </DashboardLayout>
  //   )
  // }

  return (
    <DashboardLayout>
      <div className="p-6">
        <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl max-lg:text-2xl font-bold text-gray-900 dark:text-gray-50 mb-2">{t('roles.title')}</h1>
            <p className="text-gray-600 dark:text-gray-400">{t('roles.subtitle')}</p>
          </div>
          <button
            type="button"
            onClick={openAddModal}
            className="inline-flex items-center gap-2 rounded-lg bg-[#171717] px-4 py-3 text-sm font-semibold text-white transition hover:bg-gray-800"
          >
            <PlusCircle className="w-4 h-4" />
            {t('roles.add')}
          </button>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-900">
          {loading ? (
            <div className="flex flex-col items-center justify-center gap-2">
              <Loading size={10} />  
            </div>
          ) : roles.length === 0 ? (
            <p className="text-center text-gray-500 dark:text-gray-400">{t('roles.no_roles')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm text-gray-900 dark:text-gray-100">
                <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
                  <tr>
                    <th className="px-4 py-3">{t('roles.name')}</th>
                    <th className="px-4 py-3">{t('roles.permissions_count')}</th>
                    <th className="px-4 py-3">{t('roles.users_count')}</th>
                    <th className="px-4 py-3 text-right">{t('roles.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {roles.map((role) => (
                    <tr key={role.id} className="border-b border-gray-200 dark:border-gray-700">
                      <td className="px-4 py-3 align-top font-medium">
                        <span className="inline-flex items-center gap-1.5">
                          <Shield className="w-4 h-4 text-blue-500" />
                          {role.name}
                        </span>
                      </td>
                      <td className="px-4 py-3 align-top text-gray-500">{role.permissions?.length || 0}</td>
                      <td className="px-4 py-3 align-top text-gray-500">{role.users_count || 0}</td>
                      <td className="px-4 py-3 align-top text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => openEditModal(role)}
                            className="inline-flex items-center gap-1 rounded-lg bg-gray-100 px-3 py-2 text-xs font-semibold text-gray-700 transition hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                          >
                            <SquarePen className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeleteConfirm(role)}
                            className="inline-flex items-center gap-1 rounded-lg bg-red-50 px-3 py-2 text-xs font-semibold text-red-600 transition hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setModalOpen(false)}>
          <div className="w-full max-w-2xl rounded-xl border border-gray-200 bg-white p-6 shadow-lg dark:border-gray-700 dark:bg-gray-900 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50">
                {editingRole ? t('roles.edit') : t('roles.add')}
              </h2>
              <button onClick={() => setModalOpen(false)} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <label className="block text-sm text-gray-700 dark:text-gray-300">
                <span>{t('roles.name')}</span>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-2 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-[#171717] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                  placeholder={t('roles.name_placeholder')}
                />
              </label>
              <div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('roles.permissions')}</span>
                <div className="mt-3 space-y-4">
                  {Object.entries(groupedPermissions).map(([group, perms]) => (
                    <div key={group}>
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">{t(group)}</h4>
                      <div className="grid grid-cols-2 gap-2">
                        {perms.map((perm) => (
                          <label
                            key={perm.id}
                            className={`flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer transition text-sm ${
                              selectedPerms.includes(perm.id)
                                ? 'border-blue-400 bg-blue-50 text-blue-700 dark:border-blue-600 dark:bg-blue-900/30 dark:text-blue-300'
                                : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                            }`}
                          >
                            <input
                              type="checkbox"
                              className="sr-only"
                              checked={selectedPerms.includes(perm.id)}
                              onChange={() => togglePermission(perm.id)}
                            />
                            {selectedPerms.includes(perm.id) ? (
                              <Check className="w-4 h-4 shrink-0" />
                            ) : (
                              <div className="w-4 h-4 shrink-0 rounded border border-gray-300 dark:border-gray-600" />
                            )}
                            <span className="text-xs">{t('permissions.' + perm.name)}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="w-full rounded-lg bg-[#171717] px-4 py-3 text-sm font-semibold text-white transition hover:bg-gray-800 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {saving ? t('roles.saving') : (editingRole ? t('roles.edit') : t('roles.add'))}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setDeleteConfirm(null)}>
          <div className="w-full max-w-sm rounded-xl border border-gray-200 bg-white p-6 shadow-lg dark:border-gray-700 dark:bg-gray-900" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50">{t('roles.delete_confirm')}</h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{deleteConfirm.name}</p>
            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeleteConfirm(null)}
                className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                {t('roles.cancel')}
              </button>
              <button
                type="button"
                onClick={() => handleDelete(deleteConfirm.id)}
                disabled={deleting}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700 disabled:opacity-70"
              >
                {deleting ? t('roles.deleting') : t('roles.delete_confirm_btn')}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  )
}
